import { useState } from 'react';
import NotificationBell from '../components/NotificationBell.jsx';
import { RealtimeProvider } from '../hooks/useRealtime.jsx';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import { navigationForRole, FOLDABLE_ROLES } from './navigation.js';
import LanguageSwitcher from '../components/LanguageSwitcher.jsx';
import { fileApi } from '../api/endpoints.js';
import { useBranding } from '../hooks/useBranding.jsx';
import Badge from '../components/Badge.jsx';

const AppLayout = () => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  /**
   * Collapsed categories, remembered per browser.
   *
   * Offered to the administrator only - see FOLDABLE_ROLES. Their menu is the
   * long one; every other role has a short list where a fold button would just
   * be a way to hide your own tools.
   */
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('crm.nav.collapsed') ?? '[]'));
    } catch {
      return new Set();
    }
  });

  const toggleGroup = (key) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem('crm.nav.collapsed', JSON.stringify([...next]));
      } catch {
        /* a private window just loses the preference */
      }
      return next;
    });
  // The company logo set in Settings brands the sidebar as well as the invoices.
  const { company: branding } = useBranding();

  const groups = navigationForRole(user.role);
  const foldable = FOLDABLE_ROLES.includes(user.role);
  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();

  const onSignOut = () => {
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <RealtimeProvider>
    <div className={open ? 'app-shell app-shell--nav-open' : 'app-shell'}>
      <aside className="sidebar">
        <div className="sidebar__brand">
          {branding?.logoFileId ? (
            <img className="sidebar__logo-img" src={fileApi.url(branding.logoFileId)} alt={branding.name ?? 'CRM'} />
          ) : (
            <span className="sidebar__logo">{branding?.name || 'CRM'}</span>
          )}
        </div>

        <nav className="sidebar__nav">
          {groups.map((group) => (
            <div
              key={group.key}
              className={
                foldable && collapsed.has(group.key) ? 'nav-group nav-group--collapsed' : 'nav-group'
              }
            >
              {foldable ? (
                <button
                  type="button"
                  className="nav-group__title"
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={!collapsed.has(group.key)}
                >
                  <span className="nav-group__chevron" aria-hidden="true" />
                  {t(group.labelKey)}
                  <span className="nav-group__count">{group.items.length}</span>
                </button>
              ) : (
                <h2 className="nav-group__title nav-group__title--plain">{t(group.labelKey)}</h2>
              )}
              <ul>
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => (isActive ? 'nav-link nav-link--active' : 'nav-link')}
                      onClick={() => setOpen(false)}
                    >
                      {t(item.labelKey)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <button
            type="button"
            className="btn btn--ghost topbar__burger"
            onClick={() => setOpen((value) => !value)}
            aria-label="Menu"
          >
            =
          </button>

          <div className="topbar__spacer" />

          <NotificationBell />

          <LanguageSwitcher compact />

          <div className="topbar__user">
            <span className="avatar" aria-hidden="true">{initials}</span>
            <div className="topbar__user-meta">
              <span className="topbar__user-name">
                {user.firstName} {user.lastName}
              </span>
              <Badge value={user.role} label={t(`roles.${user.role}`)} />
            </div>
          </div>

          <button type="button" className="btn btn--ghost" onClick={onSignOut}>
            {t('common.logout')}
          </button>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>

      <button
        type="button"
        className="app-shell__scrim"
        onClick={() => setOpen(false)}
        aria-hidden={!open}
        tabIndex={-1}
      />
    </div>
    </RealtimeProvider>
  );
};

export default AppLayout;
